import React, { useState, useEffect, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import './ServiceCarousel.css';
import { useTranslation } from 'react-i18next';

const ServiceCarousel = ({ images, autoPlayInterval = 5000 }) => {
  const [index, setIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const imagesRef = useRef(images);
  const timerRef = useRef(null);
  const { t } = useTranslation();

  // Verify different images for debugging
  useEffect(() => {
    // console.log("ServiceCarousel images data:", images);
    // Store the images in a ref to prevent issues with stale closures
    imagesRef.current = images;
  }, [images]);

  // Auto-play functionality
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      goToNext();
    }, autoPlayInterval);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [index, autoPlayInterval]);

  // Pause auto-play on hover
  const handleMouseEnter = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        goToNext();
      }, autoPlayInterval);
    }
  };

  const goToPrevious = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    const currentImages = imagesRef.current;
    setIndex(prevIndex => {
      const newIndex = prevIndex === 0 ? currentImages.length - 1 : prevIndex - 1;
      console.log(`Moving to previous slide: ${prevIndex} -> ${newIndex}`);
      return newIndex;
    });

    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToNext = () => {
    if (isAnimating) return;

    setIsAnimating(true);
    const currentImages = imagesRef.current;
    setIndex(prevIndex => {
      const newIndex = prevIndex === currentImages.length - 1 ? 0 : prevIndex + 1;
      // console.log(`Moving to next slide: ${prevIndex} -> ${newIndex}`);
      return newIndex;
    });

    setTimeout(() => setIsAnimating(false), 500);
  };

  const goToSlide = (slideIndex) => {
    if (isAnimating || slideIndex === index) return;

    setIsAnimating(true);
    console.log(`Going directly to slide: ${index} -> ${slideIndex}`);
    setIndex(slideIndex);

    setTimeout(() => setIsAnimating(false), 500);
  };

  return (
    <div
      className="container-Servicecarousel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Container>
        <Row className="serviceCarousel-mainrow">
          {/* First column divided into three rows */}
          <Col className="first-column">
            {/* Row for captions */}
            <Row>
              <Col>
                <div className="caption">
                  {images.map((image, idx) => (
                    <div
                      key={`caption-${idx}`}
                      className={`caption-item ${index === idx ? 'highlight' : ''}`}
                      onClick={() => goToSlide(idx)}
                    >
                      <h3>{t(image.title)}</h3>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            {/* Row for descriptions */}
            <Row>
              <Col className="servicecarousel-description-column">
                <div className="description">
                  {images.map((image, idx) => (
                    <div
                      key={`desc-${idx}`}
                      className={`description-item ${index === idx ? 'active' : 'hidden'}`}
                      aria-hidden={index !== idx}
                    >
                      {index === idx && <p>{t(image.description)}</p>}
                    </div>
                  ))}
                </div>
              </Col>
            </Row>

            {/* Navigation dots */}
            <div className="servicecarousel-dots">
              {images.map((_, idx) => (
                <span
                  key={`dot-${idx}`}
                  className={`dot ${index === idx ? 'active' : ''}`}
                  onClick={() => goToSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>

            {/* Buttons in the bottom-right corner */}
            <div className="servicecarousel-button-column">
              <button
                onClick={goToPrevious}
                className="svg-btn"
                aria-label="Previous slide"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={30} height={30}>
                  <path d="M256 504C119 504 8 393 8 256S119 8 256 8s248 111 248 248-111 248-248 248zM142.1 273l135.5 135.5c9.4 9.4 24.6 9.4 33.9 0l17-17c9.4-9.4 9.4-24.6 0-33.9L226.9 256l101.6-101.6c9.4-9.4 9.4-24.6 0-33.9l-17-17c-9.4-9.4-24.6-9.4-33.9 0L142.1 239c-9.4 9.4-9.4 24.6 0 34z" />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="svg-btn"
                aria-label="Next slide"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={30} height={30}>
                  <path d="M256 8c137 0 248 111 248 248S393 504 256 504 8 393 8 256 119 8 256 8zm113.9 231L234.4 103.5c-9.4-9.4-24.6-9.4-33.9 0l-17 17c-9.4 9.4-9.4 24.6 0 33.9L285.1 256 183.5 357.6c-9.4 9.4-9.4 24.6 0 33.9l17 17c9.4 9.4 24.6 9.4 33.9 0L369.9 273c9.4-9.4 9.4-24.6 0-34z" />
                </svg>
              </button>
            </div>
          </Col>

          {/* Column for Carousel images */}
          <Col className="carousel-imgcolumn">
            <div className="carousel" style={{ height: '640px' }}>
              {images.map((image, idx) => {
                // Log the image source for debugging
                // console.log(`Slide ${idx}:`, image.title, image.imageSrc);

                return (
                  <div
                    key={`slide-${idx}`}
                    className={`carousel-slide ${index === idx ? 'active' : ''}`}
                  >
                    {image.imageSrc ? (
                      <img
                        className="carousel-image"
                        src={image.imageSrc}
                        alt={image.title || 'Carousel image'}
                        loading={idx === 0 ? "eager" : "lazy"}
                      />
                    ) : (
                      <div className="carousel-placeholder">
                        <div className="placeholder-text">{image.title || 'Image not available'}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ServiceCarousel;